import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { FaCalendarAlt, FaClock, FaArrowLeft, FaShare, FaPrint, FaChevronRight, FaUser, FaTag, FaFolder } from 'react-icons/fa';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BlogDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [relatedPosts, setRelatedPosts] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);

  // Fetch blog details
  useEffect(() => {
    const fetchBlogDetails = async () => {
      try {
        setIsLoading(true);
        const res = await axios.get(`https://api.marketmaestroovip.in/api/blogs/${id}`);
        setBlog(res.data);
        
        // Fetch all blogs for recent and related posts
        const allBlogsRes = await axios.get('https://api.marketmaestroovip.in/api/blogs');
        
        // Set recent posts (latest 3 excluding current)
        const recent = allBlogsRes.data
          .filter(post => post.id !== parseInt(id))
          .slice(0, 3);
        setRecentPosts(recent);
        
        // Set related posts (same category)
        if (res.data.category) {
          const related = allBlogsRes.data
            .filter(post => post.category === res.data.category && post.id !== parseInt(id))
            .slice(0, 3);
          setRelatedPosts(related);
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching blog details:', err);
        setError('Failed to load blog post. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchBlogDetails();
  }, [id]);

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  // Calculate read time
  const calculateReadTime = (content) => {
    if (!content) return '1 min read';
    const wordsPerMinute = 200;
    const text = content.replace(/<[^>]*>/g, '');
    const wordCount = text.split(/\s+/).length;
    const readTime = Math.ceil(wordCount / wordsPerMinute);
    return `${readTime} min read`;
  };

  // Handle share
  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: blog.title,
        text: stripHtmlTags(blog.content, 100),
        url: window.location.href,
      });
    } else {
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  // Handle print
  const handlePrint = () => {
    window.print();
  };

  // Strip HTML tags for meta description
  const stripHtmlTags = (html, length = 150) => {
    if (!html) return '';
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    const text = tmp.textContent || tmp.innerText || '';
    return text.length > length ? text.substring(0, length) + '...' : text;
  };

  // Get category badge color
  const getCategoryColor = (category) => {
    const colors = {
      'Technology': 'bg-purple-600',
      'Security': 'bg-red-600',
      'Privacy': 'bg-green-600',
      'Guides': 'bg-blue-600',
      'News': 'bg-yellow-600',
      'Tutorial': 'bg-indigo-600'
    };
    return colors[category] || 'bg-blue-600';
  };

  if (isLoading) {
    return (
        <>
            <Header />
            <div className="min-h-screen bg-gray-50 text-gray-900 py-20">
                <div className="max-w-7xl mx-auto px-4">
                    <div className="flex justify-center items-center h-64">
                        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
  }

  if (error || !blog) {
    return (
        <>
            <Header />
            <div className="min-h-screen bg-gray-50 text-gray-900 py-20">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <div className="bg-white rounded-2xl shadow-lg p-8">
                        <p className="text-red-600 mb-4">{error || 'Blog post not found'}</p>
                        <button 
                            onClick={() => navigate('/blogs')}
                            className="px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition inline-flex items-center gap-2"
                        >
                            <FaArrowLeft /> Back to Blogs
                        </button>
                    </div>
                </div>
            </div>
            <Footer />
        </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
        <Header />
        
        {/* Page Header - Keeping exactly as you had it */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
                <div className="flex flex-col md:flex-row items-center justify-between">
                    <div>
                        <h1 className="text-3xl md:text-4xl font-bold mb-2">{blog.title}</h1>
                        <p className="text-blue-100"><span className="flex items-center gap-2">
                                <FaCalendarAlt className="text-white-800" /> 
                                {formatDate(blog.created_at)}
                            </span></p>
                        <p className="text-blue-200 text-sm mt-1">{blog.category && (
                                <span className={`inline-flex items-center gap-1 ${getCategoryColor(blog.category)} text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-wider`}>
                                    <FaFolder className="text-white text-xs" />
                                    {blog.category}
                                </span>
                            )}</p>
                    </div>
                </div>
            </div>
        </div>

        {/* Main Content Area with Two Columns */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <div className="flex flex-col lg:flex-row gap-8">
                
                {/* Left Column - Blog Content */}
                <div className="lg:w-2/3">
                    {/* Blog Title and Meta */}
            

                    {/* Featured Image */}
                    {blog.image && (
                        <div className="mb-8 rounded-xl overflow-hidden shadow-lg">
                            <img 
                                src={`https://api.marketmaestroovip.in/${blog.image}`}
                                alt={blog.title}
                                className="w-full h-auto"
                                onError={(e) => {
                                    e.target.src = 'https://images.unsplash.com/photo-1550751827-4bd374c3f58b?auto=format&fit=crop&q=80&w=1200';
                                }}
                            />
                        </div>
                    )}

                    

                    {/* Blog Content - Complete Tag Styling */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        className="blog-content prose prose-lg max-w-none bg-white rounded-xl shadow-sm p-8"
                    >
                        <style jsx>{`
                            .blog-content {
                                font-family: 'Inter', system-ui, -apple-system, sans-serif;
                                line-height: 1.7;
                                color: #374151;
                            }
                            
                            /* Headings */
                            .blog-content h1 {
                                font-size: 2.5rem;
                                font-weight: 800;
                                margin-top: 2rem;
                                margin-bottom: 1rem;
                                color: #111827;
                                line-height: 1.2;
                                border-bottom: 2px solid #e5e7eb;
                                padding-bottom: 0.5rem;
                            }
                            
                            .blog-content h2 {
                                font-size: 2rem;
                                font-weight: 700;
                                margin-top: 2rem;
                                margin-bottom: 1rem;
                                color: #1f2937;
                                line-height: 1.3;
                            }
                            
                            .blog-content h3 {
                                font-size: 1.5rem;
                                font-weight: 600;
                                margin-top: 1.5rem;
                                margin-bottom: 0.75rem;
                                color: #374151;
                                line-height: 1.4;
                            }
                            
                            .blog-content h4 {
                                font-size: 1.25rem;
                                font-weight: 600;
                                margin-top: 1.25rem;
                                margin-bottom: 0.5rem;
                                color: #4b5563;
                            }
                            
                            .blog-content h5 {
                                font-size: 1.1rem;
                                font-weight: 600;
                                margin-top: 1rem;
                                margin-bottom: 0.5rem;
                                color: #6b7280;
                            }
                            
                            .blog-content h6 {
                                font-size: 1rem;
                                font-weight: 600;
                                margin-top: 1rem;
                                margin-bottom: 0.5rem;
                                color: #9ca3af;
                                text-transform: uppercase;
                                letter-spacing: 0.05em;
                            }
                            
                            /* Paragraphs */
                            .blog-content p {
                                margin-bottom: 1.25rem;
                                color: #4b5563;
                                line-height: 1.8;
                            }
                            
                            /* Bold and Strong */
                            .blog-content strong, .blog-content b {
                                font-weight: 700;
                                color: #111827;
                            }
                            
                            /* Italic */
                            .blog-content em, .blog-content i {
                                font-style: italic;
                                color: #4b5563;
                            }
                            
                            /* Links */
                            .blog-content a {
                                color: #2563eb;
                                text-decoration: none;
                                border-bottom: 1px solid transparent;
                                transition: all 0.2s;
                            }
                            
                            .blog-content a:hover {
                                color: #1d4ed8;
                                border-bottom-color: #1d4ed8;
                            }
                            
                            /* Lists - Unordered */
                            .blog-content ul {
                                list-style-type: disc;
                                margin: 1.25rem 0;
                                padding-left: 1.5rem;
                            }
                            
                            .blog-content ul li {
                                margin-bottom: 0.5rem;
                                color: #4b5563;
                                position: relative;
                            }
                            
                            .blog-content ul li::marker {
                                color: #2563eb;
                            }
                            
                            /* Lists - Ordered */
                            .blog-content ol {
                                list-style-type: decimal;
                                margin: 1.25rem 0;
                                padding-left: 1.5rem;
                            }
                            
                            .blog-content ol li {
                                margin-bottom: 0.5rem;
                                color: #4b5563;
                            }
                            
                            .blog-content ol li::marker {
                                color: #2563eb;
                                font-weight: 600;
                            }
                            
                            /* Nested Lists */
                            .blog-content ul ul, .blog-content ol ul {
                                list-style-type: circle;
                                margin: 0.5rem 0 0.5rem 1rem;
                            }
                            
                            .blog-content ol ol, .blog-content ul ol {
                                list-style-type: lower-alpha;
                                margin: 0.5rem 0 0.5rem 1rem;
                            }
                            
                            /* Blockquotes */
                            .blog-content blockquote {
                                border-left: 4px solid #2563eb;
                                background-color: #eff6ff;
                                padding: 1rem 1.5rem;
                                margin: 1.5rem 0;
                                border-radius: 0 0.5rem 0.5rem 0;
                                font-style: italic;
                                color: #1f2937;
                            }
                            
                            .blog-content blockquote p {
                                margin-bottom: 0.5rem;
                                color: #374151;
                            }
                            
                            .blog-content blockquote p:last-child {
                                margin-bottom: 0;
                            }
                            
                            .blog-content blockquote cite {
                                display: block;
                                margin-top: 0.5rem;
                                font-size: 0.875rem;
                                color: #6b7280;
                                font-style: normal;
                            }
                            
                            /* Code */
                            .blog-content code {
                                font-family: 'Fira Code', 'Courier New', monospace;
                                background-color: #f3f4f6;
                                color: #dc2626;
                                padding: 0.2rem 0.4rem;
                                border-radius: 0.25rem;
                                font-size: 0.875rem;
                            }
                            
                            .blog-content pre {
                                background-color: #1f2937;
                                color: #f3f4f6;
                                padding: 1rem;
                                border-radius: 0.5rem;
                                overflow-x: auto;
                                margin: 1.5rem 0;
                            }
                            
                            .blog-content pre code {
                                background-color: transparent;
                                color: inherit;
                                padding: 0;
                                font-size: 0.875rem;
                                line-height: 1.6;
                            }
                            
                            /* Horizontal Rule */
                            .blog-content hr {
                                border: 0;
                                height: 1px;
                                background: linear-gradient(to right, transparent, #d1d5db, transparent);
                                margin: 2rem 0;
                            }
                            
                            /* Images */
                            .blog-content img {
                                max-width: 100%;
                                height: auto;
                                border-radius: 0.5rem;
                                margin: 1.5rem 0;
                                box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.02);
                            }
                            
                            /* Tables */
                            .blog-content table {
                                width: 100%;
                                border-collapse: collapse;
                                margin: 1.5rem 0;
                                font-size: 0.875rem;
                            }
                            
                            .blog-content th {
                                background-color: #f9fafb;
                                font-weight: 600;
                                padding: 0.75rem;
                                text-align: left;
                                border: 1px solid #e5e7eb;
                                color: #111827;
                            }
                            
                            .blog-content td {
                                padding: 0.75rem;
                                border: 1px solid #e5e7eb;
                                color: #4b5563;
                            }
                            
                            .blog-content tr:nth-child(even) {
                                background-color: #f9fafb;
                            }
                            
                            /* Definition Lists */
                            .blog-content dl {
                                margin: 1.25rem 0;
                            }
                            
                            .blog-content dt {
                                font-weight: 700;
                                color: #111827;
                                margin-top: 0.75rem;
                            }
                            
                            .blog-content dd {
                                margin-left: 1.5rem;
                                color: #4b5563;
                            }
                            
                            /* Small Text */
                            .blog-content small {
                                font-size: 0.875rem;
                                color: #6b7280;
                            }
                            
                            /* Mark/Highlight */
                            .blog-content mark {
                                background-color: #fef3c7;
                                color: #92400e;
                                padding: 0.1rem 0.2rem;
                                border-radius: 0.125rem;
                            }
                            
                            /* Subscript and Superscript */
                            .blog-content sub, .blog-content sup {
                                font-size: 0.75rem;
                                line-height: 0;
                            }
                            
                            /* Keyboard Input */
                            .blog-content kbd {
                                background-color: #1f2937;
                                color: #f3f4f6;
                                padding: 0.125rem 0.375rem;
                                border-radius: 0.25rem;
                                font-family: monospace;
                                font-size: 0.875rem;
                                border: 1px solid #4b5563;
                            }
                            
                            /* Abbreviation */
                            .blog-content abbr {
                                cursor: help;
                                text-decoration: underline dotted #2563eb;
                            }
                            
                            /* Address */
                            .blog-content address {
                                font-style: italic;
                                color: #4b5563;
                                margin: 1rem 0;
                                padding: 0.5rem;
                                background-color: #f9fafb;
                                border-radius: 0.375rem;
                            }
                            
                            /* Figure and Figcaption */
                            .blog-content figure {
                                margin: 1.5rem 0;
                            }
                            
                            .blog-content figcaption {
                                text-align: center;
                                font-size: 0.875rem;
                                color: #6b7280;
                                margin-top: 0.5rem;
                            }
                            
                            /* Details and Summary */
                            .blog-content details {
                                margin: 1rem 0;
                                padding: 0.5rem;
                                background-color: #f9fafb;
                                border-radius: 0.375rem;
                            }
                            
                            .blog-content summary {
                                font-weight: 600;
                                color: #111827;
                                cursor: pointer;
                            }
                            
                            .blog-content summary:hover {
                                color: #2563eb;
                            }
                            
                            /* Time */
                            .blog-content time {
                                color: #2563eb;
                                font-weight: 500;
                            }
                            
                            /* Data */
                            .blog-content data {
                                color: #4b5563;
                            }
                            
                            /* Ruby and Rp */
                            .blog-content ruby {
                                ruby-align: center;
                            }
                            
                            .blog-content rp {
                                color: #9ca3af;
                            }
                            
                            .blog-content rt {
                                font-size: 0.75rem;
                                color: #6b7280;
                            }
                            
                            /* Bidirectional Text */
                            .blog-content bdi, .blog-content bdo {
                                unicode-bidi: bidi-override;
                            }
                            
                            /* Span */
                            .blog-content span {
                                /* Inherit styles by default */
                            }
                            
                            /* Division */
                            .blog-content div {
                                /* Inherit styles by default */
                            }
                            
                            /* Section */
                            .blog-content section {
                                margin: 2rem 0;
                            }
                            
                            /* Article */
                            .blog-content article {
                                margin: 2rem 0;
                            }
                            
                            /* Header inside content */
                            .blog-content header {
                                margin-bottom: 1rem;
                            }
                            
                            /* Footer inside content */
                            .blog-content footer {
                                margin-top: 1rem;
                                font-size: 0.875rem;
                                color: #6b7280;
                                border-top: 1px solid #e5e7eb;
                                padding-top: 1rem;
                            }
                            
                            /* Nav inside content */
                            .blog-content nav {
                                margin: 1rem 0;
                                padding: 0.5rem;
                                background-color: #f9fafb;
                                border-radius: 0.375rem;
                            }
                            
                            /* Aside */
                            .blog-content aside {
                                background-color: #f9fafb;
                                padding: 1rem;
                                border-radius: 0.5rem;
                                margin: 1.5rem 0;
                                border-left: 4px solid #2563eb;
                            }
                            
                            /* Emoji and special characters */
                            .blog-content .emoji {
                                font-size: 1.25rem;
                                line-height: 1;
                            }
                            
                            /* Responsive adjustments */
                            @media (max-width: 768px) {
                                .blog-content {
                                    font-size: 0.95rem;
                                }
                                
                                .blog-content h1 {
                                    font-size: 2rem;
                                }
                                
                                .blog-content h2 {
                                    font-size: 1.75rem;
                                }
                                
                                .blog-content h3 {
                                    font-size: 1.35rem;
                                }
                                
                                .blog-content pre {
                                    font-size: 0.8rem;
                                }
                            }
                        `}</style>
                        
                        <div dangerouslySetInnerHTML={{ __html: blog.content }} />
                    </motion.div>
                </div>

                {/* Right Column - Recent Posts Sidebar */}
                <div className="lg:w-1/3">
                    <div className="bg-white rounded-xl shadow-lg p-6 sticky top-25">
                        <h2 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200 flex items-center gap-2">
                            <FaClock className="text-blue-600" /> Recent Posts
                        </h2>
                        
                        {recentPosts.length > 0 ? (
                            <div className="space-y-4">
                                {recentPosts.map((post) => (
                                    <Link
                                        key={post.id}
                                        to={`/blog/${post.id}`}
                                        className="flex gap-3 group hover:bg-gray-50 p-2 rounded-lg transition"
                                    >
                                        {/* Thumbnail */}
                                        <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                                            {post.image ? (
                                                <img 
                                                    src={`https://api.marketmaestroovip.in/${post.image}`}
                                                    alt={post.title}
                                                    className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-300"
                                                    onError={(e) => {
                                                        e.target.src = 'https://via.placeholder.com/64x64?text=Blog';
                                                    }}
                                                />
                                            ) : (
                                                <div className="w-full h-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs">
                                                    Blog
                                                </div>
                                            )}
                                        </div>
                                        
                                        {/* Post Info */}
                                        <div className="flex-1">
                                            <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition line-clamp-2 text-sm">
                                                {post.title}
                                            </h3>
                                            <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                                <FaCalendarAlt className="text-blue-500 text-[10px]" />
                                                {formatDate(post.created_at)}
                                            </p>
                                        </div>
                                    </Link>
                                ))}
                                
                                <Link 
                                    to="/blogs"
                                    className="mt-4 inline-flex items-center gap-1 text-blue-600 hover:text-blue-700 font-medium text-sm"
                                >
                                    View All Posts <FaChevronRight className="text-xs" />
                                </Link>
                            </div>
                        ) : (
                            <p className="text-gray-500">No recent posts available</p>
                        )}

                        {/* Related Posts Section */}
                        {relatedPosts.length > 0 && relatedPosts[0]?.id !== recentPosts[0]?.id && (
                            <>
                                <h2 className="text-xl font-bold text-gray-900 mt-8 mb-4 pb-2 border-b border-gray-200 flex items-center gap-2">
                                    <FaTag className="text-blue-600" /> Related Posts
                                </h2>
                                <div className="space-y-4">
                                    {relatedPosts.map((post) => (
                                        <Link
                                            key={post.id}
                                            to={`/blog/${post.id}`}
                                            className="flex gap-3 group hover:bg-gray-50 p-2 rounded-lg transition"
                                        >
                                            <div className="w-16 h-16 flex-shrink-0 rounded-lg overflow-hidden bg-gray-100">
                                                {post.image ? (
                                                    <img 
                                                        src={`https://api.marketmaestroovip.in/${post.image}`}
                                                        alt={post.title}
                                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                                                    />
                                                ) : (
                                                    <div className="w-full h-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white text-xs">
                                                        Blog
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex-1">
                                                <h3 className="font-semibold text-gray-900 group-hover:text-blue-600 transition line-clamp-2 text-sm">
                                                    {post.title}
                                                </h3>
                                                <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                                                    <FaCalendarAlt className="text-blue-500 text-[10px]" />
                                                    {formatDate(post.created_at)}
                                                </p>
                                            </div>
                                        </Link>
                                    ))}
                                </div>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>

        <Footer />
    </div>
  );
};

export default BlogDetails;